import {
  findStickers,
  mutateSticker,
  drawSweepstakesWinners,
  getSweepstakesWinners,
  mutateRemoveSticker,
  findSticker,
  findBullhorns,
  getMedia,
  getAllMedia,
  mutateMedia,
  mutateRemoveMedia,
  benevityCauses,
  findAllOrganizations,
  findAllSpots,
  getCMSHomeScreenData,
  mutateCMSHomeScreenData,
  mutateStickerStatus,
  postGiftSticker,
  sendSweepstakesLoserEmail,
} from '@/graphs/gql';
import { benevityCauseDetails } from '@/graphs/payment';
import { getStickerInsights, mutateStickerVarient } from '@/graphs/stickers';
import globalShared from '@/shared';

export default function commonShared(): { [key: string]: any } {
  const {
    ref,
    route,
    router,
    removeItemGetConfirm,
    makeGraphRequest,
    getAuthToken,
    dateAndTime,
    preSignedUrl,
  } = globalShared();

  async function getStickers(take = 20, skip = 0, searchString = '', filter = ['ALL'], orderBy = 'Seen', orderAsc = false, organizationId = ''): Promise<any> {
    const response = await makeGraphRequest('query', findStickers, {
      take, skip, searchString, filter, orderBy, orderAsc, organizationId,
    }, getAuthToken());

    return response.data.findStickers;
  }

  async function getSticker(id: number): Promise<any> {
    const response = await makeGraphRequest('query', findSticker, { id }, getAuthToken());
    return response.data.findSticker;
  }

  async function updateSticker(input: any) {
    if (!input.thumbnailUrl) {
      if (input.url) {
        input.thumbnailUrl = input.url;
      }

      try {
        if (input.id) {
          const existingSticker = await getSticker(input.id);
          if (existingSticker && existingSticker.thumbnailUrl) {
            input.thumbnailUrl = existingSticker.thumbnailUrl;
          }
        }
      } catch (error) {
        console.error('Error fetching existing sticker for thumbnailUrl:', error);
      }
    }

    const mutationResponse = await makeGraphRequest('mutation', mutateSticker, { input }, getAuthToken());

    // Check for GraphQL errors
    if (mutationResponse?.errors && mutationResponse.errors.length > 0) {
      const errorMessage = mutationResponse.errors[0]?.message || 'Unknown error occurred';
      throw new Error(errorMessage);
    }

    console.log('[updateSticker] mutation response varients:', JSON.stringify(
      (mutationResponse?.data?.mutateSticker?.varients || []).map((v) => ({
        id: v.id,
        title: v.title,
        whatsIncluded: v.whatsIncluded,
        selectionOptions: v.selectionOptions,
      })),
    ));

    return mutationResponse?.data?.mutateSticker;
  }

  async function sendLoserEmails(stickerId: any) {
    await makeGraphRequest('mutation', sendSweepstakesLoserEmail, { stickerId });
  }

  async function drawWinner(sweepstakesStickerId: any, participantId: any) {
    await makeGraphRequest(
      'mutation',
      drawSweepstakesWinners,
      {
        sweepstakesStickerId,
        participantIds: participantId || [],
      },
    );
  }

  async function getWinners(sweepstakesStickerId: any) {
    const response = await makeGraphRequest(
      'mutation',
      getSweepstakesWinners,
      {
        sweepstakesStickerId,
      },
    );
    return response.data.getSweepstakesWinners;
  }

  async function approveSticker(input: any) {
    await makeGraphRequest('mutation', mutateStickerStatus, { input }, getAuthToken());
  }

  async function removeSticker(id: number) {
    await makeGraphRequest('mutation', mutateRemoveSticker, { id }, getAuthToken());
  }

  async function findMedia(id: string) {
    const response = await makeGraphRequest('query', getMedia, { id }, getAuthToken());
    return response.data.getMedia;
  }

  async function findAllMedia() {
    const response = await makeGraphRequest('query', getAllMedia, {}, getAuthToken());
    return response.data.getAllMedia;
  }

  async function doMutateMedia(input: any) {
    const response = await makeGraphRequest('mutation', mutateMedia, { input }, getAuthToken());
    return response.data.mutateMedia;
  }

  async function removeMedia(id: string) {
    const response = await makeGraphRequest('mutation', mutateRemoveMedia, { id }, getAuthToken());
    return response.data.mutateMedia;
  }

  async function getBullhornsAsTags(): Promise<any> {
    const response = await makeGraphRequest('query', findBullhorns, { getfavourite: false }, getAuthToken());
    return response.data.findBullhorns;
  }

  async function getcausesAsDropdown(query: string): Promise<any> {
    const response = await makeGraphRequest('query', benevityCauses, { q: query, page: '1' }, getAuthToken());
    return response.data.benevityCauses.causes;
  }

  async function getBenevityCauseDetails(causeId: string): Promise<any> {
    const response = await makeGraphRequest('query', benevityCauseDetails, { causeId }, getAuthToken());
    return response.data.benevityCauseDetails;
  }

  async function getAllOrganizations(): Promise<any> {
    const response = await makeGraphRequest('query', findAllOrganizations, {}, getAuthToken());
    return response.data.findAllOrganizations;
  }

  async function getAllSpots(): Promise<any> {
    const response = await makeGraphRequest('query', findAllSpots, {}, getAuthToken());
    return response.data.findAllSpots;
  }

  async function fetchCMSHomeScreenData(): Promise<any> {
    const response = await makeGraphRequest('query', getCMSHomeScreenData, {}, getAuthToken());
    return response.data.getCMSHomeScreenData;
  }

  async function saveCMSHomeScreenData(input: any) {
    const response = await makeGraphRequest('mutation', mutateCMSHomeScreenData, { input }, getAuthToken());
    return response.data.mutateCMSHomeScreenData;
  }

  async function saveStickerStatusBulk(input: any) {
    const response = await makeGraphRequest('mutation', mutateStickerStatus, { input }, getAuthToken());
    return response.data.mutateStickerStatus;
  }

  async function MutateStickerVarient(input: any) {
    await makeGraphRequest('mutation', mutateStickerVarient, { input }, getAuthToken());
  }

  async function findStickerInsights(stickerId: any): Promise<any> {
    if (!stickerId) {
      console.error('findStickerInsights: stickerId is required');
      return null;
    }
    const response = await makeGraphRequest('query', getStickerInsights, { stickerId }, getAuthToken());
    return response.data.getStickerInsights;
  }

  async function giftSticker(input: any): Promise<any> {
    const response = await makeGraphRequest('mutation', postGiftSticker, { input }, getAuthToken());
    return response.data;
  }

  async function saveFeaturedSticker(stickers) {
    try {
      const currentData = await makeGraphRequest(
        'query',
        getCMSHomeScreenData,
        {},
        getAuthToken(),
      );

      if (!currentData?.data?.getCMSHomeScreenData) {
        throw new Error('Failed to fetch current home screen data');
      }

      const homeData = currentData.data.getCMSHomeScreenData;
      let metaFeaturedStickers = homeData.metaFeaturedStickers || [];

      if (Array.isArray(stickers)) {
        stickers.forEach((sticker) => {
          if (sticker.featured) {
            const existingIndex = metaFeaturedStickers.findIndex((s) => s.id === sticker.id.toString());

            if (existingIndex !== -1) {
              metaFeaturedStickers[existingIndex] = {
                ...metaFeaturedStickers[existingIndex],
                title: sticker.title || '',
                image: sticker.image || '',
                order: sticker.order,
              };
            } else {
              metaFeaturedStickers.push({
                id: sticker.id.toString(),
                title: sticker.title || '',
                image: sticker.image || '',
                order: sticker.order,
              });
            }
          } else {
            metaFeaturedStickers = metaFeaturedStickers.filter((s) => s.id !== sticker.id.toString());
          }
        });

        metaFeaturedStickers.sort((a, b) => a.order - b.order);
      } else {
        const sticker = stickers;
        if (sticker.featured) {
          const existingIndex = metaFeaturedStickers.findIndex((s) => s.id === sticker.id.toString());

          if (existingIndex !== -1) {
            metaFeaturedStickers[existingIndex] = {
              ...metaFeaturedStickers[existingIndex],
              title: sticker.title || '',
              image: sticker.image || '',
              order: sticker.order,
            };
          } else {
            metaFeaturedStickers.push({
              id: sticker.id.toString(),
              title: sticker.title || '',
              image: sticker.image || '',
              order: sticker.order,
            });
          }

          metaFeaturedStickers = metaFeaturedStickers.map((s) => {
            if (s.id === sticker.id.toString()) return s;
            if (s.order >= sticker.order) {
              return { ...s, order: s.order + 1 };
            }
            return s;
          });
        } else {
          metaFeaturedStickers = metaFeaturedStickers.filter((s) => s.id !== sticker.id.toString());
        }

        metaFeaturedStickers.sort((a, b) => a.order - b.order);
      }

      const input = {
        id: homeData.id,
        orgCollectionTitle: homeData.orgCollectionTitle || '',
        stickerCollectionTitle: homeData.stickerCollectionTitle || '',
        spotCollectionTitle: homeData.spotCollectionTitle || '',
        metaCarousel: (homeData.metaCarousel || []).map((item) => ({
          id: item.id.toString(),
          title: item.title || '',
          image: item.image || '',
          order: item.order || 0,
        })),
        metaFeaturedOrgs: (homeData.metaFeaturedOrgs || []).map((item) => ({
          id: item.id.toString(),
          title: item.title || '',
          image: item.image || '',
          order: item.order || 0,
        })),
        metaFeaturedStickers: metaFeaturedStickers.map((item) => ({
          id: item.id.toString(),
          title: item.title || '',
          image: item.image || '',
          order: item.order || 0,
        })),
        metaFeaturedSpots: (homeData.metaFeaturedSpots || []).map((item) => ({
          id: item.id.toString(),
          title: item.title || '',
          image: item.image || '',
          order: item.order || 0,
        })),
      };

      const response = await makeGraphRequest(
        'mutation',
        mutateCMSHomeScreenData,
        { input },
        getAuthToken(),
      );

      if (!response?.data?.mutateCMSHomeScreenData) {
        throw new Error('Failed to update home screen data');
      }

      return response.data.mutateCMSHomeScreenData;
    } catch (error) {
      console.error('Error in saveFeaturedSticker:', error);
      throw error;
    }
  }

  return {
    ref,
    route,
    router,
    loading: ref(false),
    dialog: ref(false),
    removeItemGetConfirm,
    makeGraphRequest,
    preSignedUrl,
    getStickers,
    getSticker,
    updateSticker,
    drawWinner,
    getWinners,
    removeSticker,
    getAuthToken,
    dateAndTime,
    findMedia,
    findAllMedia,
    doMutateMedia,
    removeMedia,
    getBullhornsAsTags,
    findBullhorns,
    getcausesAsDropdown,
    benevityCauses,
    getBenevityCauseDetails,
    getAllOrganizations,
    getAllSpots,
    fetchCMSHomeScreenData,
    saveCMSHomeScreenData,
    saveStickerStatusBulk,
    MutateStickerVarient,
    findStickerInsights,
    approveSticker,
    giftSticker,
    sendLoserEmails,
    saveFeaturedSticker,
  };
}
